import axios from "axios";
import { readBackendUrl } from "./backendConfig";

const BACKEND_URL = readBackendUrl(process.env.REACT_APP_BACKEND_URL, process.env.NODE_ENV);

/**
 * Shared axios instance for the Cruise Command 3.0 app.
 * - Always sends the auth cookies (`withCredentials: true`)
 * - Base URL is REACT_APP_BACKEND_URL — the ingress rewrites /api → :8001.
 */
export const api = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
});

// CKB-040 v4.1 · LAW-005 · client-owned view state.
//
// `selectedCruiseId` lives in `CruiseContext`. On every outbound
// `/api/cruises/current/*` request we inject `voyage_id=<selectedId>`
// automatically so no workspace has to remember. `CruiseContext` calls
// `setSelectedCruiseIdProvider(getter)` on mount; the interceptor reads
// from the getter each time so state changes propagate without stale
// closures. This is the sole mechanism by which the frontend expresses
// its chosen view context to the backend — nothing else writes it.
let _selectedCruiseIdGetter = () => null;
const _accountRequestControllers = new Set();

export function setSelectedCruiseIdProvider(getter) {
  if (typeof getter === "function") {
    _selectedCruiseIdGetter = getter;
  }
}

api.interceptors.request.use((config) => {
  if (!config.signal && !config._skipSessionTracking) {
    const controller = new AbortController();
    config.signal = controller.signal;
    config._ccAccountController = controller;
    _accountRequestControllers.add(controller);
  }
  try {
    const url = config?.url || "";
    // CKB-040 v4.1 · LAW-005 · auto-inject `voyage_id` on every
    // cruise-context-aware endpoint. Match both the canonical
    // `/api/cruises/current/…` prefix AND the traveler-context
    // endpoint that hydrates the Dashboard hero (`/api/traveler-context/
    // me/current`) — every consumer must see the caller's selected
    // cruise, not the deprecated session pointer.
    const isCruiseCurrent = /\/api\/cruises\/current(\/|$|\?)/.test(url);
    const isTravelerMeCurrent = /\/api\/traveler-context\/me\/current(\/|$|\?)/.test(url);
    const isVoyageConfig = /\/api\/voyage\/config(\/|$|\?)/.test(url);
    if (!isCruiseCurrent && !isTravelerMeCurrent && !isVoyageConfig) return config;
    const existing =
      config.params && (config.params.voyage_id || config.params["voyage_id"]);
    if (existing) return config;
    const selectedId = _selectedCruiseIdGetter && _selectedCruiseIdGetter();
    if (!selectedId) return config;
    config.params = { ...(config.params || {}), voyage_id: selectedId };
  } catch (_e) {
    // Never break the request pipeline on interceptor failure.
  }
  return config;
});

const releaseAccountController = (value) => {
  const controller = value?.config?._ccAccountController;
  if (controller) _accountRequestControllers.delete(controller);
  return value;
};

api.interceptors.response.use(
  (response) => releaseAccountController(response),
  (error) => {
    releaseAccountController(error);
    return Promise.reject(error);
  },
);

export function resetApiSessionScope() {
  for (const controller of _accountRequestControllers) controller.abort("account-session-ended");
  _accountRequestControllers.clear();
  _selectedCruiseIdGetter = () => null;
}

/**
 * Normalise a FastAPI error detail into a human-safe string so it can go
 * straight into JSX / toast without crashing React (422 returns an array
 * of objects).
 */
export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  // Structured envelopes without a public message must not become
  // "[object Object]" (or expose internal error payloads) in the interface.
  return "Something went wrong. Please try again.";
}
